<template>
  <div class="login-container">
    <!-- 背景光效层 -->
    <div class="background-overlay"></div>

    <div class="login-box">
      <!-- Logo 区域（手机端优化） -->
      <div class="logo-area">
        <div class="logo-icon">
          <i class="fas fa-shield-alt"></i>
        </div>
        <h1>额度网页H5</h1>
        <p class="subtitle">智能风控 · 安全高效</p>
      </div>

      <p class="welcome-text">欢迎回来，请登录您的账号</p>

      <!-- 用户名输入框 -->
      <div class="input-group">
        <i class="fas fa-user"></i>
        <input 
          type="text" 
          v-model="loginForm.username" 
          placeholder="用户名" 
          autocomplete="username"
        />
      </div>

      <!-- 密码输入框（带眼睛图标） -->
      <div class="input-group password-group">
        <i class="fas fa-lock"></i>
        <input 
          :type="showPassword ? 'text' : 'password'" 
          v-model="loginForm.password" 
          placeholder="密码" 
          autocomplete="current-password"
        />
        <button 
          type="button"
          class="toggle-password"
          @click="showPassword = !showPassword"
        >
          <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
        </button>
      </div>

      <div class="options">
        <label class="remember-label">
          <input type="checkbox" v-model="rememberMe" />
          <span>记住我</span>
        </label>
        <button class="login-btn" @click="login" :disabled="loading">
          <span v-if="loading">登录中...</span>
          <span v-else>立即登录 <i class="fas fa-arrow-right"></i></span>
        </button>
      </div>
    </div>

    <CustomAlert v-if="showAlert" :message="alertMessage" :type="alertType" />
    <LoadingSpinner :visible="loading" />
  </div>
</template>

<script setup>
import { ref } from "vue";
import axios from '@/axios';
import CustomAlert from '@/components/CustomAlert.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import { useRouter } from "vue-router";
import { useStore } from "vuex";

const router = useRouter();
const store = useStore();

const showAlert = ref(false);
const alertMessage = ref('');
const alertType = ref('');
const loading = ref(false);
const rememberMe = ref(false);
const showPassword = ref(false);

const loginForm = ref({
  username: '',
  password: ''
});

const showCustomAlert = (message, type = 'info') => {
  alertMessage.value = message;
  alertType.value = type;
  showAlert.value = true;
  setTimeout(() => { showAlert.value = false; }, 3000);
};

const login = async () => {
  if (!loginForm.value.username || !loginForm.value.password) {
    showCustomAlert("用户名和密码不能为空", 'error');
    return;
  }

  try {
    loading.value = true;
    const response = await axios.post('/login', loginForm.value);

    if (response.status === 200) {
      showCustomAlert(response.data.message || "登录成功", 'success');
      const token = response.data.token;
      store.dispatch('login', token);
      localStorage.setItem("loggedIn", "true");

      setTimeout(() => {
        loading.value = false;
        router.push("/admin");
      }, 1500);
    } else {
      showCustomAlert(response.data.error || "登录失败", 'error');
    }
  } catch (error) {
    showCustomAlert(error.response?.data?.error || "登录失败，请检查网络", 'error');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

.login-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #0f172a 0%, #1e2937 50%, #334155 100%);
  font-family: 'Inter', system-ui, sans-serif;
  padding: 20px 16px;   /* 手机端增加安全边距 */
}

.background-overlay {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(147, 51, 234, 0.15) 0%, transparent 50%);
  animation: glow 25s ease infinite alternate;
  pointer-events: none;
}

@keyframes glow {
  0% { opacity: 0.6; }
  100% { opacity: 1; }
}

.login-box {
  position: relative;
  background: rgba(15, 23, 42, 0.92);
  backdrop-filter: blur(24px);
  padding: 40px 28px;        /* 手机端减小左右内边距，更舒适 */
  border-radius: 24px;
  box-shadow: 
    0 25px 70px rgba(0, 0, 0, 0.55),
    inset 0 0 60px rgba(255, 255, 255, 0.06);
  width: 100%;
  max-width: 380px;          /* 手机端更窄一点，更好看 */
  text-align: center;
  border: 1px solid rgba(148, 163, 184, 0.25);
  z-index: 2;
}

/* Logo 区域优化（手机端更紧凑） */
.logo-area {
  margin-bottom: 32px;
  color: #e0f2fe;
}

.logo-icon {
  width: 68px;
  height: 68px;
  margin: 0 auto 14px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.45);
  position: relative;
}

.logo-icon::after {
  content: '';
  position: absolute;
  width: 88px;
  height: 88px;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%);
  border-radius: 50%;
  z-index: -1;
}

.logo-icon i {
  font-size: 36px;
  color: white;
}

.logo-area h1 {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 6px 0;
  letter-spacing: -0.8px;
  color: #f1f5f9;
}

.subtitle {
  color: #94a3b8;
  font-size: 14.5px;
  margin: 0;
}

.welcome-text {
  color: #cbd5e1;
  font-size: 15.5px;
  margin-bottom: 32px;
  font-weight: 500;
}

/* 输入框（手机端优化） */
.input-group {
  position: relative;
  margin-bottom: 20px;
}

.input-group i {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
  z-index: 2;
}

.input-group input {
  width: 100%;
  padding: 16px 54px 16px 54px;
  background: rgba(30, 41, 59, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 14px;
  font-size: 16px;
  color: #f1f5f9;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.input-group input:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.25);
  background: rgba(30, 41, 59, 1);
}

/* 眼睛图标（防溢出 + 手机友好） */
.password-group .toggle-password {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;
  z-index: 3;
  padding: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.password-group .toggle-password:hover {
  color: #cbd5e1;
  background: rgba(148, 163, 184, 0.2);
}

.options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #cbd5e1;
  font-size: 14.5px;
  cursor: pointer;
}

.login-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  padding: 16px 36px;
  border-radius: 14px;
  font-size: 16.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
  min-width: 150px;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 15px 35px rgba(139, 92, 246, 0.5);
}

.login-btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

/* 手机端额外适配 */
@media (max-width: 480px) {
  .login-box {
    padding: 36px 24px;
    border-radius: 20px;
  }
  
  .logo-icon {
    width: 62px;
    height: 62px;
  }
  
  .logo-icon i {
    font-size: 32px;
  }
}
</style>